import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import {
    useAvailability,
    useCreateSpace,
    useDeleteSpace,
    useDisconnectIntegration,
    useReplaceAvailability,
    useSchedulerSettings,
    useSpaces,
    useUpdateSchedulerSettings,
    useUpdateSpace,
} from "../../application/useScheduler";
import { Bell, Clock, MapPin, Plug } from "lucide-react";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import SectionTabs from "../../../../shared/ui/tabs/SectionTabs";
import type { TabDefinition } from "../../../../shared/ui/tabs/TabSwitcher";
import { startOAuth } from "../../application/oauth";
import {
    AvailabilityWindowInput,
    IntegrationProvider,
    MAX_SESSION_DURATION,
    MIN_SESSION_DURATION,
    SchedulerSettings,
    TherapistSpace,
    TherapistSpaceInput,
} from "../../domain/types";
import AvailabilityEditor from "./AvailabilityEditor";
import PlatformIcon from "./PlatformIcon";
import PatientCancellationSettings from "./PatientCancellationSettings";
import SchedulerNotificationSettings from "./SchedulerNotificationSettings";
import SpaceCard from "./SpaceCard";
import WhatsAppReminderSettings from "./WhatsAppReminderSettings";
import SpaceFormModal from "./SpaceFormModal";
import TimezonePicker from "./TimezonePicker";
import ZoomAICompanionRequirements from "./ZoomAICompanionRequirements";
import GoogleMeetRequirements from "./GoogleMeetRequirements";
import TeamsTranscriptRequirements from "./TeamsTranscriptRequirements";
import { useAiInsightsGate } from "../../../ai_insights";
import { useSessionPreferences, type SessionMode } from "../../../center";

type SettingsTab = "hours" | "integrations" | "spaces" | "notifications";

/** How long each kind of session is booked for: slots are cut to that length. */
const SESSION_DURATION_FIELDS = [
    { field: "default_duration", label: "scheduler.settings.individualDuration", mode: "individual" },
    { field: "couple_duration", label: "scheduler.settings.coupleDuration", mode: "couple" },
    { field: "group_duration", label: "scheduler.settings.groupDuration", mode: "group" },
] as const satisfies ReadonlyArray<{ field: keyof SchedulerSettings; label: string; mode: SessionMode }>;

/**
 * Everything a therapist configures about the Scheduler: working hours,
 * calendar and video integrations, and the spaces sessions are held in.
 *
 * Rendered as the Scheduler tab of practice settings, and only once the
 * therapist has adopted the tool.
 */
export default function SchedulerSettingsPanel() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    const { adopted: memoryAdopted } = useAiInsightsGate();
    // A duration only for the kinds of session the centre runs.
    const { modes } = useSessionPreferences();

    const settingsQuery = useSchedulerSettings();
    const availabilityQuery = useAvailability();
    const spacesQuery = useSpaces();

    const updateSettings = useUpdateSchedulerSettings();
    const replaceAvailability = useReplaceAvailability();
    const createSpace = useCreateSpace();
    const updateSpace = useUpdateSpace();
    const deleteSpace = useDeleteSpace();
    const disconnect = useDisconnectIntegration();

    const [windows, setWindows] = useState<AvailabilityWindowInput[]>([]);
    const [spaceModalOpen, setSpaceModalOpen] = useState(false);
    const [editingSpace, setEditingSpace] = useState<TherapistSpace | null>(null);
    const [spaceError, setSpaceError] = useState<string | null>(null);
    const [integrationError, setIntegrationError] = useState<string | null>(null);
    const [spaceToDelete, setSpaceToDelete] = useState<TherapistSpace | null>(null);

    // When the OAuth callback redirects back with ?connected=zoom, land on the
    // integrations tab so the therapist immediately sees the AI Companion setup.
    const justConnected = searchParams.get("connected");
    const [activeTab, setActiveTab] = useState<SettingsTab>(
        justConnected === "zoom" ||
            justConnected === "google" ||
            justConnected === "microsoft"
            ? "integrations"
            : "hours"
    );

    useEffect(() => {
        if (!availabilityQuery.data) return;
        // The editor works on inputs; server-assigned ids are dropped because
        // saving replaces the whole schedule.
        setWindows(
            availabilityQuery.data.map((window) => ({
                day_of_week: window.day_of_week,
                start_time: window.start_time,
                end_time: window.end_time,
                is_active: window.is_active,
            }))
        );
    }, [availabilityQuery.data]);

    if (settingsQuery.isLoading) {
        return <p role="status">{t("scheduler.common.loading")}</p>;
    }

    if (settingsQuery.isError || !settingsQuery.data) {
        return (
            <div role="alert" className="flex items-center gap-2">
                <span>{t("scheduler.common.loadError")}</span>
                <button
                    type="button"
                    onClick={() => settingsQuery.refetch()}
                    className="underline"
                >
                    {t("scheduler.common.retry")}
                </button>
            </div>
        );
    }

    const settings = settingsQuery.data;

    const submitSpace = (payload: TherapistSpaceInput) => {
        setSpaceError(null);
        const onError = () => setSpaceError(t("scheduler.settings.spaceSaveError"));
        const onSuccess = () => {
            setSpaceModalOpen(false);
            setEditingSpace(null);
        };

        if (editingSpace) {
            updateSpace.mutate(
                { spaceId: editingSpace.id, payload },
                { onSuccess, onError }
            );
        } else {
            createSpace.mutate(payload, { onSuccess, onError });
        }
    };

    const tabs: TabDefinition<SettingsTab>[] = [
        { id: "hours", label: t("scheduler.settings.tabs.hours"), icon: Clock },
        {
            id: "integrations",
            label: t("scheduler.settings.tabs.integrations"),
            icon: Plug,
        },
        { id: "spaces", label: t("scheduler.settings.tabs.spaces"), icon: MapPin },
        {
            id: "notifications",
            label: t("scheduler.settings.tabs.notifications"),
            icon: Bell,
        },
    ];

    return (
        <div className="flex flex-col gap-6">
            <SectionTabs
                tabs={tabs}
                activeTab={activeTab}
                onChange={setActiveTab}
                label={t("scheduler.settings.title")}
                className="mb-0"
            />

            {activeTab === "hours" && (
              <>
            <section className="flex max-w-2xl flex-col gap-2">
                <h2 className="text-base font-semibold">
                    {t("scheduler.settings.patientBooking")}
                </h2>
                <label className="flex items-start gap-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                    <input
                        type="checkbox"
                        className="mt-1"
                        checked={settings.patient_booking_enabled}
                        disabled={updateSettings.isPending}
                        aria-label={t("scheduler.settings.patientBookingToggle")}
                        onChange={(event) =>
                            updateSettings.mutate({
                                patient_booking_enabled: event.target.checked,
                            })
                        }
                    />
                    <span className="flex flex-col gap-1 text-sm">
                        <span className="font-medium">
                            {t("scheduler.settings.patientBookingToggle")}
                        </span>
                        <span className="text-gray-500">
                            {t("scheduler.settings.patientBookingHint")}
                        </span>
                    </span>
                </label>
            </section>

            <PatientCancellationSettings
                settings={settings}
                isSaving={updateSettings.isPending}
                onChange={(update) => updateSettings.mutate(update)}
            />

            <section className="flex max-w-md flex-col gap-4">
                <TimezonePicker
                    value={settings.timezone}
                    disabled={updateSettings.isPending}
                    onChange={(timezone) => updateSettings.mutate({ timezone })}
                />

                <fieldset className="flex flex-col gap-2 text-sm">
                    <legend className="mb-1">{t("scheduler.settings.durations")}</legend>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t("scheduler.settings.durationsHint")}
                    </p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {SESSION_DURATION_FIELDS.filter(({ mode }) => modes[mode]).map(({ field, label }) => (
                            <label key={field} className="flex flex-col gap-1">
                                {t(label)}
                                <input
                                    // Remounted when the stored value changes: a
                                    // couple's or group's length follows the
                                    // individual one until it is given its own.
                                    key={settings[field]}
                                    type="number"
                                    min={MIN_SESSION_DURATION}
                                    max={MAX_SESSION_DURATION}
                                    aria-label={t(label)}
                                    defaultValue={settings[field]}
                                    disabled={updateSettings.isPending}
                                    onBlur={(event) => {
                                        const value = Number(event.target.value);
                                        if (value !== settings[field]) {
                                            updateSettings.mutate({ [field]: value });
                                        }
                                    }}
                                    className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
                                />
                            </label>
                        ))}
                    </div>
                </fieldset>

                {updateSettings.isError && (
                    <p role="alert" className="text-sm text-red-600">
                        {t("scheduler.settings.saveError")}
                    </p>
                )}
            </section>

            <AvailabilityEditor
                windows={windows}
                onChange={setWindows}
                onSave={(next) => replaceAvailability.mutate(next)}
                isSaving={replaceAvailability.isPending}
            />

              </>
            )}

            {activeTab === "integrations" && (
            <section className="flex flex-col gap-3">
                <h2 className="text-base font-semibold">
                    {t("scheduler.settings.integrations")}
                </h2>
                <p className="text-sm text-gray-500">
                    {t("scheduler.settings.integrationsHint")}
                </p>
                <IntegrationRow
                    provider="google"
                    label={t("scheduler.settings.connectGoogle")}
                    connected={settings.google_connected}
                    // Google has not finished reviewing the Meet access yet.
                    status="inReview"
                    onDisconnect={() => disconnect.mutate("google")}
                    onConnectError={() =>
                        setIntegrationError(t("scheduler.settings.connectError"))
                    }
                />
                <IntegrationRow
                    provider="zoom"
                    label={t("scheduler.settings.connectZoom")}
                    connected={settings.zoom_connected}
                    onDisconnect={() => disconnect.mutate("zoom")}
                    onConnectError={() =>
                        setIntegrationError(t("scheduler.settings.connectError"))
                    }
                />
                <IntegrationRow
                    provider="microsoft"
                    label={t("scheduler.settings.connectMicrosoft")}
                    connected={settings.microsoft_connected}
                    // Said before connecting: a personal account is turned
                    // away at Microsoft's sign-in, with no word of why.
                    note={t("scheduler.settings.microsoftNote")}
                    status="beta"
                    onDisconnect={() => disconnect.mutate("microsoft")}
                    onConnectError={() =>
                        setIntegrationError(t("scheduler.settings.connectError"))
                    }
                />
                {integrationError && (
                    <p role="alert" className="text-sm text-red-600">
                        {integrationError}
                    </p>
                )}

                {/* Both of these exist to feed Memoria aumentada: a transcript
                    with nothing to read it is setup for no reason, so a
                    therapist without the tool is not asked to do any of it. */}
                {memoryAdopted && (
                    <section className="mt-4 flex flex-col gap-3 border-t border-gray-200 pt-5 dark:border-gray-800">
                        <h2 className="text-base font-semibold">
                            {t("scheduler.settings.aiMeetings.title")}
                        </h2>
                        <p className="text-sm text-gray-500">
                            {t("scheduler.settings.aiMeetings.hint")}
                        </p>
                        <GoogleMeetRequirements
                            defaultOpen={justConnected === "google"}
                        />
                        <ZoomAICompanionRequirements
                            defaultOpen={justConnected === "zoom"}
                        />
                        <TeamsTranscriptRequirements
                            defaultOpen={justConnected === "microsoft"}
                            connected={settings.microsoft_connected}
                            transcriptsEnabled={settings.microsoft_transcripts_enabled}
                            onEnableError={() =>
                                setIntegrationError(t("scheduler.settings.connectError"))
                            }
                        />
                    </section>
                )}
            </section>
            )}

            {activeTab === "notifications" && (
                <>
                    <SchedulerNotificationSettings
                        settings={settings}
                        isSaving={updateSettings.isPending}
                        saveFailed={updateSettings.isError}
                        onChange={(update) => updateSettings.mutate(update)}
                    />
                    <WhatsAppReminderSettings
                        settings={settings}
                        isSaving={updateSettings.isPending}
                        saveFailed={updateSettings.isError}
                        onChange={(update) => updateSettings.mutate(update)}
                    />
                </>
            )}

            {activeTab === "spaces" && (
            <section className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-base font-semibold">
                        {t("scheduler.settings.spaces")}
                    </h2>
                    <button
                        type="button"
                        onClick={() => {
                            setEditingSpace(null);
                            setSpaceModalOpen(true);
                        }}
                        className="text-sm text-brand-600"
                    >
                        {t("scheduler.settings.newSpace")}
                    </button>
                </div>

                {spacesQuery.isLoading && (
                    <p role="status">{t("scheduler.common.loading")}</p>
                )}
                {spacesQuery.data?.length === 0 && (
                    <p className="text-sm text-gray-500">
                        {t("scheduler.settings.noSpaces")}
                    </p>
                )}
                {spacesQuery.data?.map((space) => (
                    <SpaceCard
                        key={space.id}
                        space={space}
                        isDeleting={deleteSpace.isPending}
                        onEdit={(target) => {
                            setEditingSpace(target);
                            setSpaceModalOpen(true);
                        }}
                        onDelete={(target) => setSpaceToDelete(target)}
                    />
                ))}
                {deleteSpace.isError && (
                    <p role="alert" className="text-sm text-red-600">
                        {t("scheduler.settings.spaceInUse")}
                    </p>
                )}
            </section>
            )}

            {/* Deleting a space is destructive and one click away, so confirm. */}
            <ConfirmDialog
                isOpen={spaceToDelete !== null}
                destructive
                isBusy={deleteSpace.isPending}
                title={t("scheduler.settings.deleteSpaceTitle")}
                message={t("scheduler.settings.deleteSpaceMessage", {
                    name: spaceToDelete?.name ?? "",
                })}
                confirmLabel={t("scheduler.settings.deleteSpaceConfirm")}
                cancelLabel={t("scheduler.common.cancel")}
                onCancel={() => setSpaceToDelete(null)}
                onConfirm={() => {
                    if (!spaceToDelete) return;
                    deleteSpace.mutate(spaceToDelete.id, {
                        onSettled: () => setSpaceToDelete(null),
                    });
                }}
            />

            <SpaceFormModal
                open={spaceModalOpen}
                space={editingSpace}
                onClose={() => {
                    setSpaceModalOpen(false);
                    setEditingSpace(null);
                }}
                onSubmit={submitSpace}
                isSaving={createSpace.isPending || updateSpace.isPending}
                errorMessage={spaceError}
            />
        </div>
    );
}

function IntegrationRow({
    provider,
    label,
    connected,
    note,
    status,
    onDisconnect,
    onConnectError,
}: {
    provider: IntegrationProvider;
    label: string;
    connected: boolean;
    /** Shown under the row — e.g. an integration still awaiting approval. */
    note?: string;
    /** An integration not yet final, said beside its name and under the row. */
    status?: "beta" | "inReview";
    onDisconnect: () => void;
    onConnectError: () => void;
}) {
    const { t } = useTranslation();
    const [starting, setStarting] = useState(false);

    const connect = async () => {
        setStarting(true);
        try {
            // Navigates away on success, so `starting` is only reset on failure.
            await startOAuth(provider);
        } catch {
            setStarting(false);
            onConnectError();
        }
    };

    return (
        <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
            <div className="flex items-center justify-between">
            <span className="flex items-center gap-3 text-sm">
                <PlatformIcon provider={provider} />
                {label}
                {status && (
                    <span className="rounded-full bg-accent-tint px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.08em] text-accent-on-tint">
                        {t(`scheduler.settings.integrationStatus.${status}.label`)}
                    </span>
                )}
            </span>
            {connected ? (
                <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-green-600">
                        {t("scheduler.settings.connected")}
                    </span>
                    <button
                        type="button"
                        onClick={onDisconnect}
                        className="text-sm text-red-500"
                    >
                        {t("scheduler.settings.disconnect")}
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    disabled={starting}
                    onClick={connect}
                    className="rounded-lg border border-brand-500 px-3 py-1.5 text-sm font-medium text-brand-600 disabled:opacity-50 dark:text-brand-400"
                >
                    {starting
                        ? t("scheduler.settings.connecting")
                        : t("scheduler.settings.connect")}
                </button>
            )}
            </div>
            {status && (
                <p className="mt-2 text-xs text-content-tertiary">
                    {t(`scheduler.settings.integrationStatus.${status}.hint`)}
                </p>
            )}
            {note && (
                <p className="mt-2 text-xs text-content-tertiary">{note}</p>
            )}
        </div>
    );
}
